import { z } from 'zod';
import { groupBy } from '@/utils/array';
import { MS_PER_DAY, parseStrictUtcDate, parseUtcTime, toUtcDateKey } from '@/utils/date';
import { isPlainObject, stableStringify, trimStringsDeep } from '@/utils/object';
import { formatIssuePath } from '@/utils/zod';

/*
 * The market-maker program settings contract, as enforced by the scoring job
 * (mm-program `src/mm_settings/validation.py`) and by bop-api on save
 * (`serverless/src/core/mmProgram/schema.js`, the same zod v4 rules plus stricter unknown-field checks). The job validates every document
 * before it scores any of them, so this must never accept what those reject.
 * `contract-cases.json` is the shared fixture all three run against.
 */

const MM_PROGRAM_SORT_KEY = 'mm_program';
export const SESSIONS = ['C', 'E', 'W'] as const;
export const RULE_COMPONENTS = ['rest', 'linger', 'skew', 'vol', 'oob'] as const;
export const PROGRAM_TYPES = ['least_performing_bucket', 'independent_buckets'] as const;
export const DOWNTIME_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

export type Session = (typeof SESSIONS)[number];
export type RuleComponent = (typeof RULE_COMPONENTS)[number];
export type ProgramType = (typeof PROGRAM_TYPES)[number];
export type DowntimeDay = (typeof DOWNTIME_DAYS)[number];

const PERPETUAL_SUFFIX = '_USD_P';
const VOLATILITY_FIELDS = ['add_bps', 'cooldown_s', 'instrument', 'provider', 'rungs_bps'];
const OUT_OF_BOOK_FIELDS = ['full_s', 'max_cut_fraction', 'min_gap_s'];
const COMPONENT_FIELDS: Record<RuleComponent, string> = {
  rest: 'place_rest_ms',
  linger: 'fill_linger_ms',
  skew: 'max_skew_fraction',
  vol: 'volatility',
  oob: 'out_of_book',
};

export const isPerpetual = (instrument: string) => instrument.trim().toUpperCase().endsWith(PERPETUAL_SUFFIX);

const missing = (field: string) => `Missing required field '${field}'`;

const numberField = (field: string, label: string) => z.number({ error: (issue) => (issue.input === undefined ? missing(field) : `${label} must be a number`) });

const forbidden = (field: string, programType: ProgramType) => z.never({ error: `Field '${field}' is not allowed in ${programType} program` }).optional();

const exponentStartField = (context: string) =>
  numberField('exponent_start', `${context} exponent_start`)
    .min(0, `${context} exponent_start must be between 0 and 100`)
    .max(100, `${context} exponent_start must be between 0 and 100`)
    .nullable();

const exponentField = (context: string) =>
  numberField('exponent', `${context} exponent`)
    .min(0, `${context} exponent must be between 0 (inclusive) and 20 (exclusive)`)
    .lt(20, `${context} exponent must be between 0 (inclusive) and 20 (exclusive)`)
    .nullable();

const thresholdField = (context: string) =>
  numberField('threshold', `${context} threshold`).min(0, `${context} threshold must be between 0 and 100`).max(100, `${context} threshold must be between 0 and 100`);

const compensationField = (context: string) => numberField('compensation', `${context} compensation`).gt(0, `${context} compensation must be positive`);

const checkExponentPair = (value: { exponent_start?: number | null; exponent?: number | null }, context: z.RefinementCtx, label: string, path: (string | number)[] = []) => {
  const hasStart = value.exponent_start !== undefined && value.exponent_start !== null;
  const hasExponent = value.exponent !== undefined && value.exponent !== null;
  if (hasStart !== hasExponent) {
    context.addIssue({
      code: 'custom',
      message: `${label}: exponent_start and exponent must both be present or both be absent`,
      path: [...path, hasStart ? 'exponent' : 'exponent_start'],
    });
  }
};

const bucketFields = (bucket: string) => ({
  bps: numberField('bps', `Bucket ${bucket}.bps`).min(0, `Bucket ${bucket}.bps must be non-negative`),
  kpi: numberField('kpi', `Bucket ${bucket}.kpi`).min(0, `Bucket ${bucket}.kpi must be non-negative`),
  nbbo: z.boolean({ error: `Bucket ${bucket}.nbbo must be a boolean` }).nullish(),
});

const basicBucketSchema = (bucket: string) =>
  z.object(bucketFields(bucket), { error: (issue) => (issue.input === undefined ? missing(bucket) : `Bucket ${bucket} must be a dict`) });

const independentBucketSchema = (bucket: string) =>
  z
    .object(
      {
        ...bucketFields(bucket),
        threshold: thresholdField(`Bucket ${bucket}`),
        compensation: compensationField(`Bucket ${bucket}`),
        exponent_start: exponentStartField(`Bucket ${bucket}`),
        exponent: exponentField(`Bucket ${bucket}`),
      },
      { error: (issue) => (issue.input === undefined ? missing(bucket) : `Bucket ${bucket} must be a dict`) },
    )
    .superRefine((value, context) => checkExponentPair(value, context, `Bucket ${bucket}`));

/** A missing key breaks the block's exact key set; a present key of the wrong type gets `<label>.<field> must be <expected>`. */
const exactKeys = (label: string, fields: string[]) => {
  const message = `${label} must contain exactly ${fields.join(', ')}`;
  return {
    message,
    error: (field: string, expected: string) => (issue: { input?: unknown }) => (issue.input === undefined ? message : `${label}.${field} must be ${expected}`),
  };
};

const volatilityKeys = exactKeys('Rules volatility', VOLATILITY_FIELDS);
const volatilitySchema = z
  .strictObject(
    {
      provider: z
        .string({ error: volatilityKeys.error('provider', 'a non-empty string') })
        .trim()
        .min(1, 'Rules volatility.provider must be a non-empty string'),
      instrument: z
        .string({ error: volatilityKeys.error('instrument', 'a non-empty string') })
        .trim()
        .min(1, 'Rules volatility.instrument must be a non-empty string'),
      rungs_bps: z
        .array(z.number({ error: 'Rules volatility.rungs_bps must be numbers' }), { error: volatilityKeys.error('rungs_bps', 'a list') })
        .min(1, 'Rules volatility rungs_bps and add_bps must be non-empty lists'),
      add_bps: z
        .array(z.number({ error: 'Rules volatility.add_bps must be numbers' }).min(0, 'Rules volatility add_bps must be >= 0'), {
          error: volatilityKeys.error('add_bps', 'a list'),
        })
        .min(1, 'Rules volatility rungs_bps and add_bps must be non-empty lists'),
      cooldown_s: z.number({ error: volatilityKeys.error('cooldown_s', 'a number') }).gt(0, 'Rules volatility cooldown_s must be positive'),
    },
    { error: volatilityKeys.message },
  )
  .superRefine((value, context) => {
    if (value.rungs_bps.length !== value.add_bps.length) {
      context.addIssue({ code: 'custom', message: 'Rules volatility rungs_bps and add_bps must have the same length', path: ['add_bps'] });
    }
    if (value.rungs_bps[0] <= 0 || value.rungs_bps.some((rung, index) => index > 0 && rung <= value.rungs_bps[index - 1])) {
      context.addIssue({ code: 'custom', message: 'Rules volatility rungs_bps must be positive and strictly increasing', path: ['rungs_bps'] });
    }
  });

const outOfBookKeys = exactKeys('Rules out_of_book', OUT_OF_BOOK_FIELDS);
const outOfBookSchema = z.strictObject(
  {
    min_gap_s: z.number({ error: outOfBookKeys.error('min_gap_s', 'a number') }).min(0, 'Rules out_of_book.min_gap_s must be >= 0'),
    full_s: z.number({ error: outOfBookKeys.error('full_s', 'a number') }).gt(0, 'Rules out_of_book.full_s must be positive'),
    max_cut_fraction: z
      .number({ error: outOfBookKeys.error('max_cut_fraction', 'a number') })
      .min(0, 'Rules out_of_book.max_cut_fraction must be in [0, 1]')
      .max(1, 'Rules out_of_book.max_cut_fraction must be in [0, 1]'),
  },
  { error: outOfBookKeys.message },
);

const NEEDS_LIVE = "Program rules need 'live' and at least one component";
const LIVE_LIST = `Rules live must be a list of distinct components from ${RULE_COMPONENTS.join(', ')}`;

const msField = (field: string) =>
  z
    .number({ error: `Rules ${field} must be a number` })
    .min(0, `Rules ${field} must be >= 0`)
    .optional();

const rulesSchema = z
  .strictObject(
    {
      live: z
        .array(z.enum(RULE_COMPONENTS, { error: LIVE_LIST }), { error: (issue) => (issue.input === undefined ? NEEDS_LIVE : LIVE_LIST) })
        .refine((live) => new Set(live).size === live.length, LIVE_LIST),
      place_rest_ms: msField('place_rest_ms'),
      move_rest_ms: msField('move_rest_ms'),
      fill_linger_ms: msField('fill_linger_ms'),
      max_skew_fraction: z
        .number({ error: 'Rules max_skew_fraction must be a number' })
        .gt(0, 'Rules max_skew_fraction must be in (0, 1]')
        .max(1, 'Rules max_skew_fraction must be in (0, 1]')
        .optional(),
      volatility: volatilitySchema.optional(),
      out_of_book: outOfBookSchema.optional(),
    },
    {
      error: (issue) => (issue.code === 'unrecognized_keys' ? `Unknown rules fields: ${[...issue.keys].sort().join(', ')}` : 'Program rules must be a dict'),
    },
  )
  .superRefine((rules, context) => {
    if (Object.entries(rules).every(([field, value]) => field === 'live' || value === undefined)) {
      context.addIssue({ code: 'custom', message: NEEDS_LIVE, path: ['live'] });
    }
    if ((rules.place_rest_ms === undefined) !== (rules.move_rest_ms === undefined)) {
      context.addIssue({
        code: 'custom',
        message: 'Rules place_rest_ms and move_rest_ms must both be present',
        path: [rules.place_rest_ms === undefined ? 'place_rest_ms' : 'move_rest_ms'],
      });
    }
    const unset = rules.live.filter((component) => rules[COMPONENT_FIELDS[component] as keyof typeof rules] === undefined);
    if (unset.length) {
      context.addIssue({ code: 'custom', message: `Rules live lists ${unset.join(', ')} without its values`, path: ['live'] });
    }
  });

const openVolumeSchema = z.object(
  {
    b2_multiple: numberField('b2_multiple', 'ov b2_multiple').min(0, 'ov b2_multiple must be non-negative'),
    compensation_fraction: numberField('compensation_fraction', 'ov compensation_fraction')
      .min(0, 'ov compensation_fraction must be between 0 and 1')
      .max(1, 'ov compensation_fraction must be between 0 and 1'),
  },
  { error: 'ov settings must be a dict' },
);

const currencyField = (field: string) => z.literal('USD', { error: (issue) => (issue.input === undefined ? missing(field) : `Program ${field} must be USD`) });

const programDateField = (field: 'start_date' | 'end_date') =>
  z
    .string({ error: (issue) => (issue.input === undefined ? missing(field) : `Program ${field} must be a string`) })
    .trim()
    .superRefine((value, context) => {
      if (!value) {
        if (field === 'start_date') context.addIssue({ code: 'custom', message: 'Program start_date is required. A blank start is a draft and is not saved.' });
        return;
      }
      if (parseStrictUtcDate(value) === null) {
        context.addIssue({ code: 'custom', message: `Program ${field} must be a real ISO UTC date, got ${value}` });
      }
    });

const nbbSourceField = (field: string) =>
  z
    .string({ error: `Program ${field} must be a non-empty string` })
    .trim()
    .min(1, `Program ${field} must be a non-empty string`)
    .optional();

const commonProgramFields = {
  kpi_currency: currencyField('kpi_currency'),
  compensation_currency: currencyField('compensation_currency'),
  start_date: programDateField('start_date'),
  end_date: programDateField('end_date'),
  session: z.enum(SESSIONS, { error: (issue) => `Program session must be one of ${SESSIONS.join(', ')}, got ${JSON.stringify(issue.input)}` }).optional(),
  nbb_instrument: nbbSourceField('nbb_instrument'),
  nbb_provider: nbbSourceField('nbb_provider'),
  ov: openVolumeSchema.optional(),
  rules: rulesSchema.optional(),
  b3: z.never({ error: "Field 'b3' is not allowed" }).optional(),
  bonus: z.never({ error: "Field 'bonus' is not allowed" }).optional(),
};

const checkCommonProgram = (
  program: { start_date: string; end_date: string; nbb_instrument?: string; nbb_provider?: string; b1: { nbbo?: boolean | null }; b2: { nbbo?: boolean | null } },
  context: z.RefinementCtx,
) => {
  const start = parseStrictUtcDate(program.start_date);
  const end = program.end_date ? parseStrictUtcDate(program.end_date) : null;
  if (start !== null && end !== null && end <= start) {
    context.addIssue({ code: 'custom', message: `Program end_date must be after start_date, got ${program.start_date} to ${program.end_date}`, path: ['end_date'] });
  }
  const hasInstrument = program.nbb_instrument !== undefined;
  if (hasInstrument !== (program.nbb_provider !== undefined)) {
    context.addIssue({
      code: 'custom',
      message: 'Program nbb_instrument and nbb_provider must both be present or both be absent',
      path: [hasInstrument ? 'nbb_provider' : 'nbb_instrument'],
    });
  } else if (!hasInstrument && (program.b1.nbbo === true || program.b2.nbbo === true)) {
    context.addIssue({ code: 'custom', message: 'Program nbb_instrument and nbb_provider are required when NBBO is enabled', path: ['nbb_instrument'] });
  }
};

const leastPerformingProgramSchema = z
  .object({
    type: z.literal('least_performing_bucket'),
    ...commonProgramFields,
    b1: basicBucketSchema('b1'),
    b2: basicBucketSchema('b2'),
    threshold: thresholdField('Program'),
    compensation: compensationField('Program'),
    exponent_start: exponentStartField('Program').optional(),
    exponent: exponentField('Program').optional(),
  })
  .superRefine((program, context) => {
    checkCommonProgram(program, context);
    checkExponentPair(program, context, 'Program');
  });

const independentProgramSchema = z
  .object({
    type: z.literal('independent_buckets'),
    ...commonProgramFields,
    b1: independentBucketSchema('b1'),
    b2: independentBucketSchema('b2'),
    threshold: forbidden('threshold', 'independent_buckets'),
    compensation: forbidden('compensation', 'independent_buckets'),
    exponent_start: forbidden('exponent_start', 'independent_buckets'),
    exponent: forbidden('exponent', 'independent_buckets'),
  })
  .superRefine(checkCommonProgram);

const programSchema = z.discriminatedUnion('type', [leastPerformingProgramSchema, independentProgramSchema], {
  error: (issue) =>
    issue.code === 'invalid_union'
      ? `Program has unsupported type ${JSON.stringify(isPlainObject(issue.input) ? issue.input.type : undefined)}. Expected ${PROGRAM_TYPES.map((type) => `'${type}'`).join(' or ')}.`
      : 'Program must be a dict',
});

export type Program = z.output<typeof programSchema>;
export type ProgramInput = z.input<typeof programSchema>;
export type Rules = z.output<typeof rulesSchema>;

// Cross-program rules for one instrument's list. Each returns the first problem, like the Python loader.

interface DayRange {
  start: number;
  end: number | null;
}

const dayOf = (value: string) => parseStrictUtcDate(value.slice(0, 10));

const dayRange = (program: Program): DayRange => ({ start: dayOf(program.start_date) ?? 0, end: program.end_date ? dayOf(program.end_date) : null });

const activeOn = (range: DayRange, day: number) => range.start <= day && (range.end === null || day < range.end);

const hasRealDates = (program: Program) => parseStrictUtcDate(program.start_date) !== null && (!program.end_date || parseStrictUtcDate(program.end_date) !== null);

const findOverlap = (programs: Program[], instrument: string, name: string): string | null => {
  const groups = groupBy(programs, (program) => program.session ?? '');
  for (const [session, grouped] of groups) {
    const ranges = grouped.map(dayRange);
    for (let i = 0; i < ranges.length; i += 1) {
      for (let j = i + 1; j < ranges.length; j += 1) {
        const [first, second] = [ranges[i], ranges[j]];
        const overlaps = (first.end === null || second.start < first.end) && (second.end === null || first.start < second.end);
        if (overlaps) {
          const describe = (range: DayRange) => `${toUtcDateKey(range.start)} to ${range.end === null ? 'ongoing' : toUtcDateKey(range.end)}`;
          return `Overlapping programs found for ${name} on instrument ${instrument} (session=${session || 'default'}): ${describe(first)} overlaps ${describe(second)}`;
        }
      }
    }
  }
  return null;
};

/** Every setting a C/E/W session set must agree on; only bucket bps and KPIs may differ per session. */
const sharedSessionSettings = (program: Program): Record<string, unknown> => {
  const settings: Record<string, unknown> = {
    type: program.type,
    compensation_currency: program.compensation_currency,
    kpi_currency: program.kpi_currency,
    'ov.b2_multiple': program.ov?.b2_multiple ?? null,
    'ov.compensation_fraction': program.ov?.compensation_fraction ?? null,
    'b1.nbbo': program.b1.nbbo === true,
    'b2.nbbo': program.b2.nbbo === true,
    nbb_provider: program.nbb_provider ?? null,
    nbb_instrument: program.nbb_instrument ?? null,
    rules: program.rules ? { ...program.rules, live: [...program.rules.live].sort() } : null,
  };
  const fields = ['compensation', 'threshold', 'exponent_start', 'exponent'] as const;
  if (program.type === 'independent_buckets') {
    for (const bucket of ['b1', 'b2'] as const) {
      for (const field of fields) settings[`${bucket}.${field}`] = program[bucket][field] ?? null;
    }
  } else {
    for (const field of fields) settings[field] = program[field] ?? null;
  }
  return settings;
};

const findSessionSetProblem = (programs: Program[], instrument: string, name: string): string | null => {
  const sets = groupBy(
    programs.filter((program) => program.session !== undefined),
    (program) => {
      const range = dayRange(program);
      return `${range.start}|${range.end ?? ''}`;
    },
  );
  for (const [key, grouped] of sets) {
    const [start, end] = key.split('|');
    const label = `${name} on ${instrument} from ${toUtcDateKey(Number(start))} to ${end ? toUtcDateKey(Number(end)) : 'ongoing'}`;
    const ordered = [...grouped].sort((left, right) => (left.session ?? '').localeCompare(right.session ?? ''));
    const sessions = ordered.map((program) => program.session);
    if (sessions.join() !== SESSIONS.join()) {
      return `Sessioned programs for ${label} must define exactly one C, E and W program with the same dates, found ${sessions.join(', ')}`;
    }
    const reference = sharedSessionSettings(ordered[0]);
    for (const program of ordered.slice(1)) {
      const settings = sharedSessionSettings(program);
      for (const [field, value] of Object.entries(reference)) {
        if (stableStringify(settings[field]) !== stableStringify(value)) {
          return `Sessions for ${label} must share every setting except bucket bps and KPIs: ${field} is ${JSON.stringify(value)} for ${ordered[0].session} and ${JSON.stringify(settings[field])} for ${program.session}`;
        }
      }
    }
  }
  return null;
};

const coverageBounds = (programs: Program[]) => {
  const ranges = programs.map(dayRange);
  const checkStart = Math.min(...ranges.map((range) => range.start));
  const finiteEnds = ranges.flatMap((range) => (range.end === null ? [] : [range.end]));
  if (!finiteEnds.length) return { checkStart, checkEnd: Math.max(...ranges.map((range) => range.start)) };
  let checkEnd = Math.max(...finiteEnds) - MS_PER_DAY;
  const maxEnd = Math.max(...finiteEnds);
  if (ranges.some((range) => range.end === null) && ranges.some((range) => activeOn(range, maxEnd))) {
    checkEnd = Math.max(checkEnd, maxEnd);
  }
  return { checkStart, checkEnd };
};

const findCoverageProblem = (programs: Program[], instrument: string, name: string): string | null => {
  const sessioned = programs.filter((program) => program.session !== undefined);
  if (!sessioned.length) return null;
  const plain = programs.filter((program) => program.session === undefined);
  const { checkStart, checkEnd } = coverageBounds(programs);
  for (let day = checkStart; day <= checkEnd; day += MS_PER_DAY) {
    const activeSessioned = sessioned.filter((program) => activeOn(dayRange(program), day));
    const activePlain = plain.filter((program) => activeOn(dayRange(program), day));
    if (activeSessioned.length && activePlain.length) return `Cannot mix sessioned and non-sessioned programs for ${name} on ${instrument} on ${toUtcDateKey(day)}`;
    if (activePlain.length > 1) return `Expected exactly 1 active non-sessioned program for ${name} on ${instrument} on ${toUtcDateKey(day)}, found ${activePlain.length}`;
    if (!activeSessioned.length) continue;
    for (const session of SESSIONS) {
      const count = activeSessioned.filter((program) => program.session === session).length;
      if (count !== 1) {
        return `Session coverage gap for ${name} on ${instrument} on ${toUtcDateKey(day)} (session=${session}): expected exactly 1 active program, found ${count}`;
      }
    }
  }
  return null;
};

const emailField = (field: string, message: string) =>
  z
    .string({ error: (issue) => (issue.input === undefined ? missing(field) : message) })
    .trim()
    .pipe(z.email({ error: message }));

const DOWNTIME_REASON_MAX = 200;

const utcTimeField = (field: 'start_time' | 'end_time') =>
  z
    .string({ error: (issue) => (issue.input === undefined ? missing(field) : 'Time must be HH:MM in UTC') })
    .trim()
    .refine((value) => parseUtcTime(value) !== null, { error: (issue) => `Invalid UTC time ${JSON.stringify(issue.input)}` });

/** Any ISO-8601 instant in, UTC out, as bop-api stores it. */
const utcInstantField = (field: 'start' | 'end') =>
  z
    .string({ error: (issue) => (issue.input === undefined ? missing(field) : `${field} must be an ISO timestamp`) })
    .datetime({ offset: true, error: `${field} must be an ISO timestamp` })
    .transform((value) => new Date(value).toISOString());

const UTC_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A recurring rule's optional bound, a UTC day that exists (not 2026-02-30); absent is open. */
const utcDayField = (field: 'start_date' | 'end_date') =>
  z
    .string({ error: `Downtime ${field} must be a YYYY-MM-DD day` })
    .trim()
    .refine((value) => UTC_DAY.test(value) && parseStrictUtcDate(value) !== null, `Downtime ${field} must be a YYYY-MM-DD day`)
    .optional();

const downtimeRuleError = (issue: { code?: string; keys?: string[] }) =>
  issue.code === 'unrecognized_keys' ? `Unknown downtime fields: ${[...(issue.keys ?? [])].sort().join(', ')}` : 'Downtime rule must be a dict';

const downtimeBaseFields = {
  id: z.uuid({ error: (issue) => (issue.input === undefined ? missing('id') : 'id must be a UUID') }),
  enabled: z.boolean({ error: (issue) => (issue.input === undefined ? missing('enabled') : 'enabled must be a boolean') }),
  reason: z
    .string({ error: (issue) => (issue.input === undefined ? missing('reason') : 'Reason must be a string') })
    .trim()
    .min(1, 'Reason must be a non-empty string')
    .max(DOWNTIME_REASON_MAX, `Reason must be at most ${DOWNTIME_REASON_MAX} characters`),
};

const recurringDowntimeRuleSchema = z
  .strictObject(
    {
      type: z.literal('recurring'),
      ...downtimeBaseFields,
      days: z
        .array(z.enum(DOWNTIME_DAYS, { error: 'Each day must be MON through SUN' }), { error: 'days must be a list' })
        .min(1, 'At least one weekday is required')
        .refine((days) => new Set(days).size === days.length, 'days must not contain duplicates'),
      start_time: utcTimeField('start_time'),
      end_time: utcTimeField('end_time'),
      start_date: utcDayField('start_date'),
      end_date: utcDayField('end_date'),
    },
    { error: downtimeRuleError },
  )
  .superRefine((rule, context) => {
    const start = parseUtcTime(rule.start_time);
    if (start !== null && start === parseUtcTime(rule.end_time)) {
      context.addIssue({ code: 'custom', message: 'start_time and end_time must differ', path: ['end_time'] });
    }
    if (rule.start_date && rule.end_date && rule.end_date <= rule.start_date) {
      context.addIssue({ code: 'custom', message: 'Downtime end_date must be after start_date', path: ['end_date'] });
    }
  });

const oneOffDowntimeRuleSchema = z
  .strictObject(
    {
      type: z.literal('one_off'),
      ...downtimeBaseFields,
      start: utcInstantField('start'),
      end: utcInstantField('end'),
    },
    { error: downtimeRuleError },
  )
  .superRefine((rule, context) => {
    if (rule.end <= rule.start) context.addIssue({ code: 'custom', message: 'Downtime end must be after start', path: ['end'] });
  });

export const downtimeRuleSchema = z.discriminatedUnion('type', [recurringDowntimeRuleSchema, oneOffDowntimeRuleSchema], {
  error: () => 'Downtime rule type must be recurring or one_off',
});

export type DowntimeRuleInput = z.input<typeof downtimeRuleSchema>;

/** Downtime is keyed by a programs instrument, and rule ids are unique across the whole document. */
const checkDowntime = (marketMaker: { programs: Record<string, unknown> | null; downtime?: Record<string, { id: string }[]> }, context: z.RefinementCtx) => {
  const programKeys = new Set(Object.keys(marketMaker.programs ?? {}));
  const seen = new Set<string>();
  for (const [instrument, rules] of Object.entries(marketMaker.downtime ?? {})) {
    if (!programKeys.has(instrument)) {
      context.addIssue({ code: 'custom', message: `downtime instrument ${JSON.stringify(instrument)} must match a programs key`, path: ['downtime', instrument] });
    }
    rules.forEach((rule, index) => {
      if (seen.has(rule.id)) context.addIssue({ code: 'custom', message: `Duplicate downtime rule id ${rule.id}`, path: ['downtime', instrument, index, 'id'] });
      seen.add(rule.id);
    });
  }
};

export const marketMakerSchema = z
  .strictObject(
    {
      p: z.string({ error: (issue) => (issue.input === undefined ? missing('p') : 'PID must be a string') }).min(1, 'PID must be a string'),
      s: z.literal(MM_PROGRAM_SORT_KEY, { error: (issue) => (issue.input === undefined ? missing('s') : `Sort key must be '${MM_PROGRAM_SORT_KEY}', got ${String(issue.input)}`) }),
      name: z
        .string({ error: (issue) => (issue.input === undefined ? missing('name') : 'Name must be a non-empty string') })
        .trim()
        .min(1, 'Name must be a non-empty string'),
      email: emailField('email', 'Email must be a valid non-empty string'),
      bcc_emails: z.array(emailField('bcc_emails', 'Each BCC email must be a valid email string'), {
        error: (issue) => (issue.input === undefined ? missing('bcc_emails') : 'Bcc_emails must be a list'),
      }),
      active: z.boolean({ error: (issue) => (issue.input === undefined ? missing('active') : 'Active must be a boolean') }),
      fast_id: z.int({ error: (issue) => (issue.input === undefined ? missing('fast_id') : 'Fast ID must be an integer') }),
      programs: z
        .record(z.string().trim().min(1, 'Instrument must be a non-empty string'), z.array(programSchema, { error: 'Program list must be a list' }), {
          error: (issue) => (issue.input === undefined ? missing('programs') : 'Programs must be a dict or null'),
        })
        .nullable(),
      downtime: z.record(z.string(), z.array(downtimeRuleSchema, { error: 'Downtime rule list must be a list' }), { error: () => 'downtime must be a dict' }).optional(),
    },
    {
      error: (issue) => (issue.code === 'unrecognized_keys' ? `Unknown market maker fields: ${[...issue.keys].sort().join(', ')}` : 'Market maker must be a dict'),
    },
  )
  .superRefine((marketMaker, context) => {
    for (const [instrument, programs] of Object.entries(marketMaker.programs ?? {})) {
      programs.forEach((program, index) => {
        if (isPerpetual(instrument)) return;
        if (program.rules) {
          context.addIssue({
            code: 'custom',
            message: `Program rules only apply to perpetual (*${PERPETUAL_SUFFIX}) markets, not ${instrument}`,
            path: ['programs', instrument, index, 'rules'],
          });
        }
        if (program.ov) {
          context.addIssue({ code: 'custom', message: `ov settings are only allowed on perpetual markets, got ${instrument}`, path: ['programs', instrument, index, 'ov'] });
        }
      });
      if (!programs.every(hasRealDates)) continue;
      const problem =
        findOverlap(programs, instrument, marketMaker.name) ??
        findSessionSetProblem(programs, instrument, marketMaker.name) ??
        findCoverageProblem(programs, instrument, marketMaker.name);
      if (problem) context.addIssue({ code: 'custom', message: problem, path: ['programs', instrument] });
    }
    checkDowntime(marketMaker, context);
  });

export type MarketMaker = z.output<typeof marketMakerSchema>;
export type MarketMakerInput = z.input<typeof marketMakerSchema>;

interface SettingsProblem {
  path: string;
  message: string;
}

/** Validates a stored or edited document the way bop-api does on save: strings are trimmed first. */
export const findSettingsProblems = (document: unknown): SettingsProblem[] => {
  const result = marketMakerSchema.safeParse(trimStringsDeep(document));
  return result.success ? [] : result.error.issues.map((issue) => ({ path: formatIssuePath(issue.path), message: issue.message }));
};
